import React, { FC, useState } from 'react';
import cx from 'classnames';
import { groupBy, size, sortBy, startCase } from 'lodash';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import none from 'file-loader!@/assets/images/none.png'; // eslint-disable-line
import fallback from 'file-loader!@/assets/images/fallback-sample.png'; // eslint-disable-line
import { Option } from '@/types/utils';
import SampleSelector, { SampleOption } from '@/components/Common/SampleSelector';
import ButtonGroup from '@/components/Common/ButtonGroup';
import { VariationView } from '@/types/product';
import { type Props } from './types';
import { getVariationViews } from './utils';
import * as styles from '../ProductConfigurator.module.scss';

type Dimensions = string | null;

// This "variation view" is a workaround to artificially group products during product selection
const BundleOptionProductSelectorMaterialDimensions: FC<Props> = ({
  className,
  option,
  value,
  onChange,
  characteristicIndex,
}) => {
  const variationViews = getVariationViews(option);
  const selectedProduct = option.products.find((product) => product.sku === value);
  const [selectedGroup, setSelectedGroup] = useState<Dimensions | undefined>(
    selectedProduct?.pim?.dimensions
  );
  const noneOption: SampleOption = { code: null, name: i18n._(t`None`), image: none };
  const options: (SampleOption & {
    dimensions: Dimensions;
  })[] = [
    ...sortBy(option.products, 'position').map((product) => ({
      code: product.sku,
      name:
        startCase(
          variationViews[1] === VariationView.Image
            ? product.label
            : (product?.pim?.characteristics?.[characteristicIndex ?? 0]
                ?.materials?.[0] ?? '')
        ) ?? product.name,
      image: product.pim?.thumbnail?.childImageSharp?.gatsbyImageData || fallback,
      dimensions: product?.pim?.dimensions ?? null,
    })),
  ];

  const groupedByDimensions = groupBy(options, 'dimensions');
  const groupOptions = Object.keys(groupedByDimensions).map(
    (key) => [key ? startCase(key) : 'None', key] as Option
  );
  const filteredOptions = options.filter((o) => o.dimensions === selectedGroup);
  const selectedOption = options.find((o) => o.code === value);

  const handleSelectGroup = (group: Dimensions) => {
    setSelectedGroup(group);
    const firstAvailable = options.filter((o) => o.dimensions === group)?.[0];
    onChange(firstAvailable?.code ?? null);
  };

  return (
    <>
      {size(groupedByDimensions) > 1 ? (
        <ButtonGroup
          className={cx(styles.buttonGroup, className)}
          label=""
          aria-label={i18n._(t`Select group`)}
          defaultValue={selectedGroup ?? undefined}
          options={groupOptions}
          onChange={handleSelectGroup}
        />
      ) : null}
      <SampleSelector
        className={cx(styles.samples, className)}
        options={[...(!option.isRequired ? [noneOption] : []), ...filteredOptions]}
        selectedOption={selectedOption}
        onChange={(option) => onChange(option.code)}
        shouldShowCode={false}
      />
    </>
  );
};

export default BundleOptionProductSelectorMaterialDimensions;
